import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { useContext } from 'react'

import { darkTheme, theme as lightTheme } from 'styles/theme'

interface ThemeContextType {
  theme: 'light' | 'dark'
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_LS_KEY = 'app-theme'

const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initialTheme = (localStorage.getItem(THEME_LS_KEY) as 'light' | 'dark') || 'light'
  const [theme, setTheme] = useState<'light' | 'dark'>(initialTheme)

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_LS_KEY) as 'light' | 'dark'
    if (savedTheme) {
      setTheme(savedTheme)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(THEME_LS_KEY, theme)
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'))
  }, [])

  const currentTheme = theme === 'light' ? lightTheme : darkTheme

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <StyledThemeProvider theme={currentTheme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  )
}

const useAppTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export { ThemeContext, ThemeProvider, useAppTheme }
